import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { FilteredProductPositionActionHandlers } from "./useProductPositionActionHandlers"

export interface UseProductPositionActionsArgs
  extends Partial<FilteredProductPositionActionHandlers> {
  idPrefix: string
  isReadonly?: boolean
  isEditDisabled?: boolean
  isDuplicateDisabled?: boolean
  isCopyDisabled?: boolean
  isAssignAttachmentsDisabled?: boolean
  isAddClarificationRequestDisabled?: boolean
  isDeleteDisabled?: boolean
  isEditConfigurationDisabled?: boolean
  isSelectInstallationReferencesDisabled?: boolean
}

export function useProductPositionActions({
  idPrefix,
  isReadonly,
  onEdit,
  isEditDisabled,
  onDuplicate,
  isDuplicateDisabled,
  onCopy,
  isCopyDisabled,
  onAssignAttachments,
  isAssignAttachmentsDisabled,
  onAddClarificationRequest,
  isAddClarificationRequestDisabled,
  onDelete,
  isDeleteDisabled,
  onEditConfiguration,
  isEditConfigurationDisabled,
  onSelectInstallationReferences,
  isSelectInstallationReferencesDisabled
}: UseProductPositionActionsArgs) {
  const { t } = useTranslation()

  const editAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__edit`,
      "data-testid": "product-position__actions__edit",
      icon: IconName.EDIT,
      label: t("ProductPosition.Actions.Update"),
      disabled: isEditDisabled,
      onClick: onEdit ?? undefined
    }),
    [idPrefix, t, onEdit, isEditDisabled]
  )

  const duplicateAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__duplicate`,
      "data-testid": "product-position__actions__duplicate",
      icon: IconName.DUPLICATE,
      label: t("ProductPosition.Actions.Duplicate"),
      disabled: isDuplicateDisabled,
      onClick: onDuplicate ?? undefined
    }),
    [idPrefix, t, onDuplicate, isDuplicateDisabled]
  )

  const copyAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__copy`,
      "data-testid": "product-position__actions__copy",
      icon: IconName.SHARE,
      label: t("ProductPosition.Actions.Copy"),
      disabled: isCopyDisabled,
      onClick: onCopy ?? undefined
    }),
    [t, onCopy, isCopyDisabled]
  )

  const deleteAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__delete`,
      "data-testid": "product-position__actions__delete",
      icon: IconName.BIN,
      label: t("ProductPosition.Actions.Delete"),
      isDanger: true,
      disabled: isDeleteDisabled,
      onClick: onDelete ?? undefined
    }),
    [idPrefix, t, onDelete, isDeleteDisabled]
  )

  const editConfigurationAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__edit-configuration`,
      "data-testid": "product-position__actions__edit-configuration",
      icon: IconName.EDIT,
      label: t("Product.Configuration.ChangeConfiguration"),
      disabled: isEditConfigurationDisabled,
      onClick: onEditConfiguration ?? undefined
    }),
    [idPrefix, t, onEditConfiguration, isEditConfigurationDisabled]
  )

  const assignAttachmentsAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__assign-attachments`,
      "data-testid": "product-position__actions__assign-attachments",
      icon: IconName.PAPERCLIP,
      label: t("ProductPosition.Actions.AssignAttachments"),
      disabled: isAssignAttachmentsDisabled,
      onClick: onAssignAttachments ?? undefined
    }),
    [t, idPrefix, isAssignAttachmentsDisabled, onAssignAttachments]
  )

  const addClarificationRequestAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__add-clarification-request`,
      "data-testid": "product-position__actions__add-clarification-request",
      icon: IconName.CLARIFICATION_REQUEST,
      label: t("ProductPosition.Actions.AddClarificationRequest"),
      disabled: isAddClarificationRequestDisabled,
      onClick: onAddClarificationRequest ?? undefined
    }),
    [t, idPrefix, isAddClarificationRequestDisabled, onAddClarificationRequest]
  )

  const selectInstallationReferencesAction = useMemo(
    () => ({
      id: `${idPrefix}__product-position__actions__select-installation-references`,
      "data-testid":
        "product-position__actions__select-installation-references",
      icon: IconName.EDIT,
      label: t("InstallationReference.SelectInstallationReferences"),
      disabled: isSelectInstallationReferencesDisabled,
      onClick: onSelectInstallationReferences ?? undefined
    }),
    [
      idPrefix,
      t,
      onSelectInstallationReferences,
      isSelectInstallationReferencesDisabled
    ]
  )

  const allActions = useMemo<Action[]>(
    () => [
      editConfigurationAction,
      editAction,
      selectInstallationReferencesAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction
    ],
    [
      editAction,
      selectInstallationReferencesAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction,
      editConfigurationAction
    ]
  )

  const readonlyActions = useMemo<Action[]>(() => [copyAction], [copyAction])

  const actions: Action[] = useMemo(
    () =>
      (isReadonly ? readonlyActions : allActions).filter(
        action => !!action.onClick
      ),
    [allActions, readonlyActions, isReadonly]
  )

  return {
    actions,
    editAction,
    duplicateAction,
    copyAction,
    assignAttachmentsAction,
    addClarificationRequestAction,
    deleteAction,
    editConfigurationAction,
    selectInstallationReferencesAction
  }
}
